import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BookOpen, LogOut, Users, FileCheck, Clock } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const [submissions, setSubmissions] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [submissionsRes, statsRes] = await Promise.all([
          axios.get(`${API}/admin/submissions`, { withCredentials: true }),
          axios.get(`${API}/admin/stats`, { withCredentials: true }),
        ]);
        setSubmissions(submissionsRes.data);
        setStats(statsRes.data);
      } catch (error) {
        toast.error("Failed to load data");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleStatusUpdate = async (submissionId, newStatus) => {
    try {
      await axios.patch(
        `${API}/admin/submissions/${submissionId}/status`,
        { status: newStatus },
        { withCredentials: true }
      );
      toast.success("Status updated successfully");
      fetchData();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const getStatusBadge = (status) => {
    const variants = {
      pending: { variant: "secondary", label: "Pending" },
      in_progress: { variant: "default", label: "In Progress" },
      completed: { variant: "success", label: "Completed" },
    };
    const config = variants[status] || variants.pending;
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      <Header />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            <Card className="bg-white">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm font-medium text-gray-600">Total Submissions</CardTitle>
                  <FileCheck className="h-5 w-5 text-blue-600" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-gray-900">{stats.total_submissions}</div>
              </CardContent>
            </Card>

            <Card className="bg-white">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm font-medium text-gray-600">Pending Review</CardTitle>
                  <Clock className="h-5 w-5 text-orange-600" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-gray-900">{stats.pending_submissions}</div>
              </CardContent>
            </Card>

            <Card className="bg-white">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm font-medium text-gray-600">By Type</CardTitle>
                  <Users className="h-5 w-5 text-teal-600" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Methodological:</span>
                    <span className="font-semibold">{stats.by_type.methodological}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Training:</span>
                    <span className="font-semibold">{stats.by_type.training}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Grants:</span>
                    <span className="font-semibold">{stats.by_type.grants}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Data Analysis:</span>
                    <span className="font-semibold">{stats.by_type.data_analysis}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">General Enquiries:</span>
                    <span className="font-semibold">{stats.by_type.general_enquiries}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Submissions Table */}
        <Card className="bg-white">
          <CardHeader>
            <CardTitle >All Submissions</CardTitle>
            <CardDescription>Manage and update submission statuses</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              </div>
            ) : submissions.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-500">No submissions yet</p>
              </div>
            ) : (
              <div className="space-y-4">
                {submissions.map((submission) => (
                  <Card key={submission.id} data-testid={`admin-submission-${submission.id}`} className="border bg-gray-50">
                    <CardContent className="pt-6">
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div className="md:col-span-2">
                          <div className="flex items-center space-x-2 mb-2">
                            <Badge className="bg-blue-600">{submission.submission_type}</Badge>
                            {getStatusBadge(submission.status)}
                          </div>
                          <p className="text-sm text-gray-600 mb-1">
                            <strong>Submitted by:</strong> {submission.user?.name} ({submission.user?.email})
                          </p>
                          <p className="text-xs text-gray-500">
                            {formatDate(submission.created_at)}
                          </p>
                        </div>

                        <div className="md:col-span-1">
                          <p className="text-sm mb-2">
                            <strong>Contact:</strong> {submission.contact_name}
                            <br />
                            <strong>Email:</strong> {submission.contact_email}
                            {submission.contact_affiliation && (
                              <>
                                <br />
                                <strong>Affiliation:</strong> {submission.contact_affiliation}
                              </>
                            )}
                          </p>
                          {submission.research_title && (
                            <p className="text-sm">
                              <strong>Title:</strong> {submission.research_title}
                            </p>
                          )}
                          {submission.training_needs && (
                            <p className="text-sm">
                              <strong>Needs:</strong> {submission.training_needs.substring(0, 50)}...
                            </p>
                          )}
                          {submission.number_of_participants && (
                            <p className="text-sm">
                              <strong>Participants:</strong> {submission.number_of_participants}
                            </p>
                          )}
                          {submission.tentative_dates && (
                            <p className="text-sm">
                              <strong>Dates:</strong> {submission.tentative_dates}
                            </p>
                          )}
                          {submission.grant_idea && (
                            <p className="text-sm">
                              <strong>Idea:</strong> {submission.grant_idea}
                            </p>
                          )}
                          {submission.analysis_type && (
                            <p className="text-sm">
                              <strong>Type:</strong> {submission.analysis_type}
                            </p>
                          )}
                          {submission.message && (
                            <p className="text-sm">
                              <strong>Message:</strong> {submission.message.substring(0, 80)}...
                            </p>
                          )}
                          {submission.affiliation_organisation && (
                            <p className="text-sm">
                              <strong>Organisation:</strong> {submission.affiliation_organisation}
                            </p>
                          )}
                          {submission.deadline && (
                            <p className="text-sm">
                              <strong>Deadline:</strong> {submission.deadline}
                            </p>
                          )}
                          {submission.attached_file && (
                            <p className="text-sm mt-1">
                              <strong>File:</strong>{" "}
                              <a
                                href={`${API}/files/${submission.attached_file.filename}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:underline"
                              >
                                {submission.attached_file.original_filename}
                              </a>
                            </p>
                          )}
                        </div>

                        <div className="md:col-span-1">
                          <Label className="text-xs text-gray-600 mb-2 block">Update Status</Label>
                          <Select
                            value={submission.status}
                            onValueChange={(value) => handleStatusUpdate(submission.id, value)}
                          >
                            <SelectTrigger data-testid={`status-select-${index}`} className="w-full">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="pending">Pending</SelectItem>
                              <SelectItem value="in_progress">In Progress</SelectItem>
                              <SelectItem value="completed">Completed</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

import { Label } from "@/components/ui/label";
import Header from "@/components/Header";
export default AdminDashboard;